import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('utility:videoPlayerErrorHandler');

export interface VideoPlayerErrorContext {
  storyId?: string;
  storyTitle?: string;
  playbackId?: string;
  videoSource: 'mux' | 'direct-mp4';
  videoUrl?: string;
  posterUrl?: string;
}

export interface VideoPlayerErrorCategories {
  isDemuxerError: boolean;
  isNetworkError: boolean;
  isDecodeError: boolean;
  isNotSupportedError: boolean;
  isAbortError: boolean;
  isBufferError: boolean;
  isManifestError: boolean;
  isHlsError: boolean;
  isDrmError: boolean;
}

/**
 * Handles video player errors from Mux Player or native HTML5 video elements.
 * Logs comprehensive error information and provides targeted diagnostics.
 *
 * @param event - The error event from the video player
 * @param context - Additional context about the video being played
 */
export function handleVideoPlayerError(
  event: Event,
  context: VideoPlayerErrorContext,
): void {
  const customEvent = event as CustomEvent;
  const error =
    customEvent?.detail || (event.target as HTMLVideoElement)?.error || event;
  const errorMessage =
    (error as MediaError)?.message ||
    (error as Error)?.message ||
    String(error);
  const errorCode = (error as MediaError)?.code || (error as Error)?.name || '';

  // Categorize error types
  const errorCategories: VideoPlayerErrorCategories = {
    isDemuxerError: errorMessage.includes('DEMUXER_ERROR_COULD_NOT_PARSE'),
    isNetworkError:
      errorMessage.includes('NETWORK') ||
      errorMessage.includes('MEDIA_ERR_NETWORK'),
    isDecodeError:
      errorMessage.includes('DECODE') ||
      errorMessage.includes('MEDIA_ERR_DECODE'),
    isNotSupportedError:
      errorMessage.includes('NOT_SUPPORTED') ||
      errorMessage.includes('MEDIA_ERR_SRC_NOT_SUPPORTED'),
    isAbortError:
      errorMessage.includes('ABORT') ||
      errorMessage.includes('MEDIA_ERR_ABORTED'),
    isBufferError: errorMessage.includes('BUFFER'),
    isManifestError: errorMessage.includes('MANIFEST'),
    isHlsError: errorMessage.includes('HLS'),
    isDrmError:
      errorMessage.includes('DRM') || errorMessage.includes('ENCRYPTED'),
  };

  // MediaError codes: 1=ABORTED, 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED
  let mediaErrorType = 'UNKNOWN';
  if ((error as MediaError)?.code) {
    const code = (error as MediaError).code;
    switch (code) {
      case 1:
        mediaErrorType = 'MEDIA_ERR_ABORTED';
        errorCategories.isAbortError = true;
        break;
      case 2:
        mediaErrorType = 'MEDIA_ERR_NETWORK';
        errorCategories.isNetworkError = true;
        break;
      case 3:
        mediaErrorType = 'MEDIA_ERR_DECODE';
        errorCategories.isDecodeError = true;
        break;
      case 4:
        mediaErrorType = 'MEDIA_ERR_SRC_NOT_SUPPORTED';
        errorCategories.isNotSupportedError = true;
        break;
    }
  }

  // Log all video player errors with comprehensive context
  logger.error('Video player error occurred', {
    errorMessage,
    errorCode,
    mediaErrorType,
    ...context,
    ...errorCategories,
    fullError: error,
  });

  // Log specific error types with targeted warnings
  if (errorCategories.isDemuxerError) {
    logger.warn('DEMUXER_ERROR_COULD_NOT_PARSE - video stream parsing failed', {
      storyId: context.storyId,
      playbackId: context.playbackId,
      videoSource: context.videoSource,
      recommendation: 'Check video encoding format and codec compatibility',
    });
  } else if (errorCategories.isNetworkError) {
    logger.warn('Network error loading video', {
      storyId: context.storyId,
      playbackId: context.playbackId,
      videoSource: context.videoSource,
      recommendation: 'Check network connectivity and CDN availability',
    });
  } else if (errorCategories.isDecodeError) {
    logger.warn('Video decode error', {
      storyId: context.storyId,
      playbackId: context.playbackId,
      videoSource: context.videoSource,
      recommendation: 'Video file may be corrupted or use unsupported codec',
    });
  } else if (errorCategories.isNotSupportedError) {
    logger.warn('Video format not supported', {
      storyId: context.storyId,
      playbackId: context.playbackId,
      videoSource: context.videoSource,
      recommendation: 'Browser or device does not support this video format',
    });
  } else if (errorCategories.isManifestError) {
    logger.warn('Manifest/playlist error', {
      storyId: context.storyId,
      playbackId: context.playbackId,
      recommendation: 'HLS/DASH manifest may be malformed or unavailable',
    });
  }
}
